/**
 * useMaterialEditor Hook
 * 
 * Material editing system dengan:
 * - Material property changes (color, metalness, roughness, etc)
 * - Material type switching (Basic, Lambert, Standard, Physical)
 * - Integration dengan undo/redo
 * 
 * @param {Object} undoRedo - Undo/redo hook result
 * @returns {Object} Material editor state dan methods
 */

import { useState, useCallback } from 'react'

export function useMaterialEditor(undoRedo) {
  const [selectedMaterial, setSelectedMaterial] = useState(null)

  // Handle material property changes
  const handleMaterialChange = useCallback(
    (property, value, skipStateUpdate = false) => {
      if (!selectedMaterial?.material) return

      const material = selectedMaterial.material
      const THREE = window.THREE_MODULE

      // Save snapshot BEFORE change untuk color/emissive
      if ((property === 'color' || property === 'emissive') && material[property]) {
        const currentHex = `#${material[property].getHexString()}`
        const normalizedCurrent = currentHex.toLowerCase()
        const normalizedNew = value.toLowerCase()

        if (normalizedCurrent !== normalizedNew) {
          const descMap = {
            color: 'Before base color change',
            emissive: 'Before emissive color change',
          }
          undoRedo.saveSnapshot(
            material,
            descMap[property] || 'Before color change',
            true // skipDebounce
          )
        }
      }

      // Apply change
      switch (property) {
        case 'color':
          if (material.color) {
            material.color.set(value)
          }
          break
        case 'metalness':
          if (material.metalness !== undefined) {
            material.metalness = value
          }
          break
        case 'roughness':
          if (material.roughness !== undefined) {
            material.roughness = value
          }
          break
        case 'emissive':
          if (material.emissive) {
            material.emissive.set(value)
          }
          break
        case 'emissiveIntensity':
          if (material.emissiveIntensity !== undefined) {
            material.emissiveIntensity = value
          }
          break
        case 'opacity':
          material.opacity = value
          material.transparent = value < 1
          break
        case 'wireframe':
          material.wireframe = value
          break
        default:
          break
      }

      material.needsUpdate = true

      if (!skipStateUpdate) {
        setSelectedMaterial((prev) => ({ ...prev }))
      }
    },
    [selectedMaterial, undoRedo]
  )

  // Switch material type
  const switchMaterialType = useCallback(
    (newType) => {
      if (!selectedMaterial?.mesh || !window.THREE_MODULE) return

      const THREE = window.THREE_MODULE
      const mesh = selectedMaterial.mesh
      const oldMaterial = selectedMaterial.material

      // Store common properties
      const commonProps = {
        color: oldMaterial.color
          ? oldMaterial.color.clone()
          : new THREE.Color(0xffffff),
        opacity: oldMaterial.opacity !== undefined ? oldMaterial.opacity : 1,
        transparent: oldMaterial.transparent || false,
        wireframe: oldMaterial.wireframe || false,
        side:
          oldMaterial.side !== undefined ? oldMaterial.side : THREE.FrontSide,
      }

      // Store maps
      const maps = {
        map: oldMaterial.map,
        normalMap: oldMaterial.normalMap,
        roughnessMap: oldMaterial.roughnessMap,
        metalnessMap: oldMaterial.metalnessMap,
        aoMap: oldMaterial.aoMap,
        emissiveMap: oldMaterial.emissiveMap,
      }

      let newMaterial

      switch (newType) {
        case 'basic':
          newMaterial = new THREE.MeshBasicMaterial({
            color: commonProps.color,
            map: maps.map,
            opacity: commonProps.opacity,
            transparent: commonProps.transparent,
            wireframe: commonProps.wireframe,
            side: commonProps.side,
          })
          break

        case 'lambert':
          newMaterial = new THREE.MeshLambertMaterial({
            color: commonProps.color,
            map: maps.map,
            emissive: oldMaterial.emissive
              ? oldMaterial.emissive.clone()
              : new THREE.Color(0x000000),
            emissiveMap: maps.emissiveMap,
            opacity: commonProps.opacity,
            transparent: commonProps.transparent,
            wireframe: commonProps.wireframe,
            side: commonProps.side,
          })
          break

        case 'standard':
          newMaterial = new THREE.MeshStandardMaterial({
            color: commonProps.color,
            map: maps.map,
            normalMap: maps.normalMap,
            roughnessMap: maps.roughnessMap,
            metalnessMap: maps.metalnessMap,
            aoMap: maps.aoMap,
            emissive: oldMaterial.emissive
              ? oldMaterial.emissive.clone()
              : new THREE.Color(0x000000),
            emissiveMap: maps.emissiveMap,
            emissiveIntensity: oldMaterial.emissiveIntensity || 0,
            roughness: oldMaterial.roughness !== undefined ? oldMaterial.roughness : 0.5,
            metalness: oldMaterial.metalness !== undefined ? oldMaterial.metalness : 0,
            opacity: commonProps.opacity,
            transparent: commonProps.transparent,
            wireframe: commonProps.wireframe,
            side: commonProps.side,
            envMapIntensity: oldMaterial.envMapIntensity || 1,
          })
          break

        case 'physical':
          newMaterial = new THREE.MeshPhysicalMaterial({
            color: commonProps.color,
            map: maps.map,
            normalMap: maps.normalMap,
            roughnessMap: maps.roughnessMap,
            metalnessMap: maps.metalnessMap,
            aoMap: maps.aoMap,
            emissive: oldMaterial.emissive
              ? oldMaterial.emissive.clone()
              : new THREE.Color(0x000000),
            emissiveMap: maps.emissiveMap,
            emissiveIntensity: oldMaterial.emissiveIntensity || 0,
            roughness: oldMaterial.roughness !== undefined ? oldMaterial.roughness : 0.5,
            metalness: oldMaterial.metalness !== undefined ? oldMaterial.metalness : 0,
            opacity: commonProps.opacity,
            transparent: commonProps.transparent,
            wireframe: commonProps.wireframe,
            side: commonProps.side,
            envMapIntensity: oldMaterial.envMapIntensity || 1,
            clearcoat: oldMaterial.clearcoat || 0,
            clearcoatRoughness: oldMaterial.clearcoatRoughness || 0,
          })
          break

        default:
          return
      }

      // Apply to mesh
      if (Array.isArray(mesh.material)) {
        const index = mesh.material.indexOf(oldMaterial)
        if (index !== -1) {
          mesh.material[index] = newMaterial
        }
      } else {
        mesh.material = newMaterial
      }

      // Dispose old material
      oldMaterial.dispose()

      // Update state
      setSelectedMaterial({
        mesh: mesh,
        material: newMaterial,
        meshName: selectedMaterial.meshName,
      })

      console.log(`Switched material to ${newType}:`, mesh.name)
    },
    [selectedMaterial]
  )

  return {
    selectedMaterial,
    setSelectedMaterial,
    handleMaterialChange,
    switchMaterialType,
  }
}

